import React from "react"
import PropTypes from "prop-types"
import { Box } from "@mui/material"
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined"
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined"
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined"
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined"
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined"
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined"
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined"
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined"
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined"
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined"
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined"

import { COLORS } from "./constants/colors"

export const OVERVIEW_METRIC_ICON_ACCENTS = [
  "info",
  "success",
  "warning",
  "error",
  "default",
  "violet",
]

export const OVERVIEW_METRIC_ICON_PRESETS = {
  objectiveCost: {
    Icon: TrackChangesOutlinedIcon,
    accent: "info",
  },
  objectiveMargin: {
    Icon: TrendingUpOutlinedIcon,
    accent: "success",
  },
  saleAmount: {
    Icon: ReceiptOutlinedIcon,
    accent: "warning",
  },
  plannedSale: {
    Icon: ReceiptOutlinedIcon,
    accent: "info",
  },
  registeredAdvance: {
    Icon: BarChartOutlinedIcon,
    accent: "violet",
  },
  workAdvancePercent: {
    Icon: PercentOutlinedIcon,
    accent: "warning",
  },
  expectedCostAtDate: {
    Icon: TrackChangesOutlinedIcon,
    accent: "default",
  },
  costDeviationAtDate: {
    Icon: WarningAmberOutlinedIcon,
    accent: "error",
  },
  finalExpectedMargin: {
    Icon: TrendingUpOutlinedIcon,
    accent: "success",
  },
  expensesTotal: {
    Icon: LayersOutlinedIcon,
    accent: "info",
  },
  expensesImputed: {
    Icon: TaskAltOutlinedIcon,
    accent: "success",
  },
  expensesToImpute: {
    Icon: PendingActionsOutlinedIcon,
    accent: "warning",
  },
  expensesPercentImputed: {
    Icon: PercentOutlinedIcon,
    accent: "default",
  },
  eacCost: {
    Icon: ShowChartOutlinedIcon,
    accent: "info",
  },
  eacVsObjective: {
    Icon: CompareArrowsOutlinedIcon,
    accent: "default",
  },
  invoicingTotal: {
    Icon: ReceiptOutlinedIcon,
    accent: "info",
  },
  invoicingPending: {
    Icon: HourglassEmptyOutlinedIcon,
    accent: "warning",
  },
  invoicingPct: {
    Icon: PercentOutlinedIcon,
    accent: "violet",
  },
  invoicingCertifiedPct: {
    Icon: BarChartOutlinedIcon,
    accent: "success",
  },
  collectionsGap: {
    Icon: AccountBalanceWalletOutlinedIcon,
    accent: "warning",
  },
  collectionsTotalCollected: {
    Icon: AccountBalanceWalletOutlinedIcon,
    accent: "success",
  },
  collectionsPendingCollection: {
    Icon: HourglassEmptyOutlinedIcon,
    accent: "warning",
  },
  collectionsCollectedPct: {
    Icon: PercentOutlinedIcon,
    accent: "violet",
  },
  collectionsOverdue: {
    Icon: WarningAmberOutlinedIcon,
    accent: "error",
  },
  evolutionAccumulatedExpenses: {
    Icon: LayersOutlinedIcon,
    accent: "error",
  },
  evolutionAccumulatedCertifications: {
    Icon: BarChartOutlinedIcon,
    accent: "info",
  },
  evolutionAccumulatedInvoicing: {
    Icon: ReceiptOutlinedIcon,
    accent: "success",
  },
  evolutionGapCertifiedNotInvoiced: {
    Icon: ReceiptOutlinedIcon,
    accent: "info",
  },
  evolutionGapInvoicedNotCollected: {
    Icon: AccountBalanceWalletOutlinedIcon,
    accent: "success",
  },
}

const getPalette = accent => COLORS[accent] ?? COLORS.default

const OverviewMetricIcon = ({
  Icon,
  accent = "default",
  size = 44,
  iconSize = 24,
}) => {
  const palette = getPalette(accent)

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backgroundColor: palette.background,
        color: palette.color,
      }}
    >
      <Icon sx={{ fontSize: iconSize }} />
    </Box>
  )
}

OverviewMetricIcon.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  accent: PropTypes.oneOf(OVERVIEW_METRIC_ICON_ACCENTS),
  size: PropTypes.number,
  iconSize: PropTypes.number,
}

OverviewMetricIcon.defaultProps = {
  accent: "default",
  size: 44,
  iconSize: 24,
}

export default OverviewMetricIcon
